import { useMemo, useState, type FormEvent } from 'react';
import { Button, Dialog, Input, Label } from '../components/ui';
import {
    useBulkClearFlatworkScope,
    useBulkCompleteFlatwork,
} from '../lib/flatworkSchedule';
import { FLATWORK_TYPE_LABELS } from '../lib/database.types';
import type { FlatworkEvent, FlatworkType, JobWithContext } from '../lib/database.types';

const FLATWORK_TYPES: FlatworkType[] = ['driveway', 'walkway', 'apron', 'city_sidewalk'];

function todayInput(): string {
    return new Date().toISOString().slice(0, 10);
}

interface BulkFlatworkDialogProps {
    open: boolean;
    onClose: () => void;
    jobs: JobWithContext[];
    flatworkByJob: Map<string, FlatworkEvent[]>;
}

/**
 * Flatwork actions across a selection.
 *
 * Exists because 315 lots sit on the board flagged as owing flatwork with no
 * flatwork rows, so each has to be opened and marked by hand. It is also the
 * answer to Andrea's townhome ask: "if we get a townhome job that has 7
 * building units, instead of me clicking in to every box to add the date and
 * the status".
 *
 * Two actions, deliberately separate. Marking complete records work that was
 * done. Clearing scope says the lot never had flatwork. Both take a lot off the
 * board and they mean different things, so writing completed driveway rows on a
 * foundation-only lot is not an acceptable shortcut for the second.
 */
export function BulkFlatworkDialog({ open, onClose, jobs, flatworkByJob }: BulkFlatworkDialogProps) {
    const completeMut = useBulkCompleteFlatwork();
    const clearMut = useBulkClearFlatworkScope();

    const [types, setTypes] = useState<Set<FlatworkType>>(
        () => new Set<FlatworkType>(['driveway'])
    );
    const [completionDate, setCompletionDate] = useState(todayInput());
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<string | null>(null);
    const [confirmingClear, setConfirmingClear] = useState(false);

    // Split the selection by what the RPC will actually do with each lot. The
    // counts matter: lots that already carry rows keep the types recorded on
    // them and ignore whatever is ticked below.
    const { withRows, withoutRows, alreadyComplete } = useMemo(() => {
        let withRows = 0;
        let withoutRows = 0;
        let alreadyComplete = 0;
        for (const j of jobs) {
            const fw = flatworkByJob.get(j.id) ?? [];
            if (fw.length === 0) {
                withoutRows += 1;
            } else {
                withRows += 1;
                if (fw.every((f) => f.status === 'complete')) alreadyComplete += 1;
            }
        }
        return { withRows, withoutRows, alreadyComplete };
    }, [jobs, flatworkByJob]);

    const clearableCount = useMemo(
        () => jobs.filter((j) => (flatworkByJob.get(j.id) ?? []).length === 0).length,
        [jobs, flatworkByJob]
    );

    function toggleType(t: FlatworkType) {
        setTypes((prev) => {
            const next = new Set(prev);
            if (next.has(t)) next.delete(t);
            else next.add(t);
            return next;
        });
    }

    async function handleComplete(e: FormEvent) {
        e.preventDefault();
        setError(null);
        setResult(null);

        if (!completionDate) {
            setError('Pick a completion date.');
            return;
        }
        if (withoutRows > 0 && types.size === 0) {
            setError(
                `${withoutRows} of these lots have no flatwork recorded yet, so pick at least one type to create for them.`
            );
            return;
        }

        try {
            const res = await completeMut.mutateAsync({
                p_job_ids: jobs.map((j) => j.id),
                p_completion_date: completionDate,
                p_event_types: FLATWORK_TYPES.filter((t) => types.has(t)),
            });
            const parts = [
                `${res.jobs_created} lot${res.jobs_created === 1 ? '' : 's'} recorded`,
                `${res.jobs_completed} updated`,
            ];
            if (res.jobs_skipped > 0) parts.push(`${res.jobs_skipped} already complete, left alone`);
            setResult(parts.join(', ') + '.');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not mark these complete');
        }
    }

    async function handleClearScope() {
        setError(null);
        setResult(null);
        try {
            const res = await clearMut.mutateAsync(jobs.map((j) => j.id));
            const refused = res.refused_has_rows?.length ?? 0;
            setResult(
                `${res.jobs_cleared} lot${res.jobs_cleared === 1 ? '' : 's'} taken off the board` +
                (refused > 0 ? `, ${refused} refused because they already have flatwork recorded.` : '.')
            );
            setConfirmingClear(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not clear these');
            setConfirmingClear(false);
        }
    }

    const busy = completeMut.isPending || clearMut.isPending;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title="Flatwork on selected lots"
            subtitle={`${jobs.length} lot${jobs.length === 1 ? '' : 's'} selected`}
            maxWidth="lg"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        {result ? 'Done' : 'Cancel'}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleComplete}
                        disabled={busy || jobs.length === 0}
                        type="submit"
                        form="bulk-flatwork-form"
                    >
                        {completeMut.isPending ? 'Marking...' : 'Mark complete'}
                    </Button>
                </>
            }
        >
            <form id="bulk-flatwork-form" onSubmit={handleComplete} className="space-y-4">
                {/* What is about to happen, per lot, before anything is clicked. */}
                <div className="border border-graphite-200 dark:border-graphite-800 rounded px-3 py-2.5 space-y-1">
                    {withoutRows > 0 ? (
                        <p className="text-xs text-graphite-700 dark:text-graphite-300">
                            <span className="font-mono tabular font-semibold">{withoutRows}</span> with
                            no flatwork recorded yet. The types you pick below get created on these and
                            marked complete.
                        </p>
                    ) : null}
                    {withRows > 0 ? (
                        <p className="text-xs text-graphite-700 dark:text-graphite-300">
                            <span className="font-mono tabular font-semibold">{withRows}</span> already
                            have flatwork recorded. Their existing types are kept exactly as they are
                            and only the completion date is set.
                        </p>
                    ) : null}
                    {alreadyComplete > 0 ? (
                        <p className="text-xs text-graphite-500">
                            <span className="font-mono tabular">{alreadyComplete}</span> of those are
                            already complete and will be left untouched.
                        </p>
                    ) : null}
                </div>

                <div>
                    <Label>Types to record on lots with nothing yet</Label>
                    <div className="grid grid-cols-2 gap-2">
                        {FLATWORK_TYPES.map((t) => (
                            <label
                                key={t}
                                className="flex items-center gap-2 px-2.5 py-2 border border-graphite-300 dark:border-graphite-700 rounded cursor-pointer select-none hover:bg-graphite-100 dark:hover:bg-graphite-900"
                            >
                                <input
                                    type="checkbox"
                                    checked={types.has(t)}
                                    onChange={() => toggleType(t)}
                                    className="accent-signal-500"
                                    disabled={busy}
                                />
                                <span className="text-sm text-graphite-800 dark:text-graphite-200">
                                    {FLATWORK_TYPE_LABELS[t]}
                                </span>
                            </label>
                        ))}
                    </div>
                    <p className="font-mono text-[10px] text-graphite-500 mt-1">
                        Pick what these lots actually had. This writes a record of work performed.
                    </p>
                </div>

                <div className="max-w-[200px]">
                    <Label htmlFor="bulk-completion-date">Completion date</Label>
                    <Input
                        id="bulk-completion-date"
                        type="date"
                        value={completionDate}
                        onChange={(e) => setCompletionDate(e.target.value)}
                        disabled={busy}
                    />
                </div>

                {error ? (
                    <div className="border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                        {error}
                    </div>
                ) : null}

                {result ? (
                    <div className="border-l-2 border-done-500 bg-done-500/10 px-3 py-2 text-sm text-done-700 dark:text-done-400">
                        {result}
                    </div>
                ) : null}
            </form>

            {/* The other case: lots that never had flatwork at all. The flag is
                set whenever a Pour is completed, regardless of whether the lot
                has any flatwork in its contract, so foundation-only lots get
                stuck on the board too. */}
            {clearableCount > 0 ? (
                <div className="mt-5 pt-4 border-t border-graphite-200 dark:border-graphite-800">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400">
                                No flatwork on these lots
                            </p>
                            <p className="text-xs text-graphite-500 mt-1">
                                {confirmingClear
                                    ? `Takes ${clearableCount} lot${clearableCount === 1 ? '' : 's'} off the board by saying they never had flatwork, rather than recording work that was never done. Lots that already have flatwork recorded are refused.`
                                    : 'Use this for foundation-only lots that are stuck on the board. It clears the flatwork flag instead of writing a completed driveway that never existed.'}
                            </p>
                        </div>
                        {confirmingClear ? (
                            <div className="flex gap-2 shrink-0">
                                <Button variant="ghost" onClick={() => setConfirmingClear(false)} disabled={busy}>
                                    Cancel
                                </Button>
                                <Button variant="danger" onClick={handleClearScope} disabled={busy}>
                                    {clearMut.isPending ? 'Clearing...' : 'Confirm'}
                                </Button>
                            </div>
                        ) : (
                            <Button
                                variant="secondary"
                                onClick={() => setConfirmingClear(true)}
                                disabled={busy}
                                className="shrink-0"
                            >
                                No flatwork
                            </Button>
                        )}
                    </div>
                </div>
            ) : null}
        </Dialog>
    );
}
